import { CommonModule } from "@angular/common";
import { Component, Inject, OnInit } from "@angular/core";
import { FormControl, FormGroup, ReactiveFormsModule } from "@angular/forms";
import { MAT_DIALOG_DATA, MatDialogModule } from "@angular/material/dialog";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatSelectModule } from "@angular/material/select";
import { CONSTANTS } from "../../../../Constants/Constants";
import { MatButtonModule } from "@angular/material/button";
import { TranslatePipe } from "@ngx-translate/core";
import { IAppliedOfficeRates } from "../../../../models/properties/IAppliedOffice";
import { MultilingualCurrencyPipe } from "../../../../pipes/multilingual-currency-pipe/multilingual-currency.pipe";
import { LaborRates } from "../../../../models/dtos";
import { CommonFunctionsService } from "../../../../services/common-functions.service";
import { CloseOnEscapeDirective } from "../../../../directives/close-on-escape.directive";

@Component({
    selector: 'app-sol-properties-labor-rates-applied-offices',
    templateUrl: './sol-properties-labor-rates-applied-offices.component.html',
    styleUrls: ['./sol-properties-labor-rates-applied-offices.component.scss'],
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        MatDialogModule,
        MatFormFieldModule,
        MatSelectModule,
        MatButtonModule,
        TranslatePipe,
        MultilingualCurrencyPipe,
        CloseOnEscapeDirective
    ]
})
export class SolPropertiesLaborRatesAppliedOfficesComponent implements OnInit {
    officeOptions: LaborRates[] = [];
    salesOfficeNames: Map<number, string> = new Map();
    selectedRates: IAppliedOfficeRates = { pm: 0, eng: 0, eeng: 0, leng: 0, spec: 0, mech: 0, elec: 0, ceng: 0, qeng: 0, grap: 0, prog: 0, neng: 0, dds: 0 };

    isStandardRatesVisible: boolean = true;
    isEngOfficeRatesVisible: boolean = false;
    isZineRatesVisible: boolean = false;

    appliedOfficesForm: FormGroup<{
        officeSelectionOption: FormControl<number>;
    }> =  new FormGroup({
        officeSelectionOption: new FormControl<number>(CONSTANTS.ZERO)
    });

    constructor(
        @Inject(MAT_DIALOG_DATA) private data: { rates: LaborRates[], salesOfficeNames: Map<number, string> },
        private readonly commonFunctionsService: CommonFunctionsService
    ) {}

    ngOnInit(): void {
        this.loadAppliedOfficesData();
    }

    getOfficeName(salesOfficeId: number): string {
        return this.salesOfficeNames.get(salesOfficeId) ?? salesOfficeId.toString();
    }

    onOfficeSelectionChange(salesOfficeId: number): void {
        const rates = this.officeOptions.find(r => r.salesOfficeId === salesOfficeId);
        this.assignRatesVisibility(salesOfficeId);
        this.selectedRates = this.mapLaborRates(rates);
    }

    private assignRatesVisibility(salesOfficeId: number): void {
        this.isZineRatesVisible = this.commonFunctionsService.isZineOffice(salesOfficeId);
        this.isEngOfficeRatesVisible = this.commonFunctionsService.isEngOffice(salesOfficeId);
        this.isStandardRatesVisible = this.commonFunctionsService.isStandardOffice(salesOfficeId);
    }

    private loadAppliedOfficesData(): void {
        this.officeOptions = this.data?.rates ?? [];
        this.salesOfficeNames = this.data?.salesOfficeNames ?? new Map();

        if (this.officeOptions.length) {
            this.appliedOfficesForm.patchValue({ officeSelectionOption: this.officeOptions[0].salesOfficeId });
            this.assignRatesVisibility(this.officeOptions[0].salesOfficeId);
            this.selectedRates = this.mapLaborRates(this.officeOptions[0]);
        }
    }

    private mapLaborRates(rates: LaborRates | null | undefined): IAppliedOfficeRates {
        return {
            pm: rates?.projectManagementRates ?? 0,
            eng: rates?.engineeringRates ?? 0,
            eeng: rates?.energyEngineeringRates ?? 0,
            leng: rates?.lEngineeringRates ?? 0,
            spec: rates?.specialistRates ?? 0,
            mech: rates?.mechanicalRates ?? 0,
            elec: rates?.electricianRates ?? 0,
            ceng: rates?.cyberSecEngineeringRates ?? 0,
            qeng: rates?.qualificationEngineeringRates ?? 0,
            grap: rates?.graphicsRates ?? 0,
            prog: rates?.programmingRates ?? 0,
            neng: rates?.networkEngineeringRates ?? 0,
            dds: rates?.ddsRates ?? 0
        };
    }
}
